import React from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";

import { Icon, IconSize } from "@gaming/xds-react-js";
import { Lock } from "@gaming/xds-react-js-icons";

import { PartnerPageLayout } from "../../components/PartnerPageLayout";
import { PitchesManager } from "../../components/PitchesManager";
import { xboxOnboardSiteLocationPrefix } from "../../constants";
import { getImage, ImageType } from "../../helpers/imageHelper";
import {
  PartnerAccessLevel,
  PartnerAccountRequirement,
  usePartnerRouteGuard,
} from "../../helpers/usePartnerRouteGuard";
import { useStrings } from "../../localization/localizationHelpers";
import { getXboxOnboardData } from "../../redux/selectors/xboxOnboardSelectors";
import * as styles from "./PitchListPage.module.css";

// Convert *>bold<*> markers to <strong> and <br/> to <br/>
const formatRichText = (text: string | undefined): string => {
  if (!text) {
    return "";
  }
  return text.replace(/\*>(.*?)<\*>/g, "<strong>$1</strong>").replace(/<br\s*\/?>/g, "<br/>");
};

const PitchListPage: React.FC = () => {
  const isPageReady = usePartnerRouteGuard(
    PartnerAccessLevel.NdaSigned,
    PartnerAccountRequirement.NonAaa
  );
  const xboxOnboardData = useSelector(getXboxOnboardData);
  const [
    pitchIntroTitle,
    pitchMainSubtitle,
    pitchSubtitle,
    beforeYouStartTitle,
    beforeYouStartText,
    pitchProgramBreadcrumbLabel,
    pitchProgramLogoAlt,
    pitchProgramInfoTitle,
    approvalRequiredTitle,
    approvalRequiredDescription,
    approvalRequiredLink,
  ] = useStrings(
    "dashboardPitchIntroTitle",
    "pitchConceptDashboardMainSubtitle",
    "pitchConceptDashboardSubtitle",
    "pitchConceptBeforeYouStartBoxTitle",
    "pitchConceptBeforeYouStartBoxText",
    "pitchListProgramBreadcrumbLabel",
    "pitchListProgramLogoAlt",
    "pitchListProgramInfoTitle",
    "pitchListApprovalRequiredTitle",
    "pitchListApprovalRequiredDescription",
    "pitchListApprovalRequiredLink"
  );

  if (!isPageReady) {
    return null;
  }

  const isIdXboxApproved = xboxOnboardData?.isIdXboxApproved === true;
  const canSubmitIdApplication = xboxOnboardData?.hubPermissions.canSubmitIdApplication === true;

  return (
    <>
      <PartnerPageLayout breadcrumbLabel={pitchProgramBreadcrumbLabel}>
        <div className={styles.pageHeader}>
          <h1 className={styles.pageTitle}>{pitchIntroTitle}</h1>
          <p className={styles.pageSubtitle}>{pitchMainSubtitle}</p>
        </div>

        <div className={styles.programInfoCard}>
          <img
            src={getImage("pitchlogo", ImageType.Png)}
            alt={pitchProgramLogoAlt}
            className={styles.programInfoImage}
          />
          <div className={styles.programInfoText}>
            <h2 className={styles.programInfoTitle}>{pitchProgramInfoTitle}</h2>
            <div
              className={styles.programInfoDescription}
              dangerouslySetInnerHTML={{ __html: formatRichText(pitchSubtitle) }}
            />
          </div>
        </div>

        <div className={styles.beforeYouStartCard}>
          <div className={styles.beforeYouStartHeader}>
            <h2 className={styles.beforeYouStartTitle}>{beforeYouStartTitle}</h2>
          </div>
          <hr className={styles.beforeYouStartDivider} />
          <div
            className={styles.beforeYouStartBody}
            dangerouslySetInnerHTML={{ __html: formatRichText(beforeYouStartText) }}
          />
        </div>

        {isIdXboxApproved ? (
          <PitchesManager />
        ) : (
          <section className={styles.approvalCard} aria-labelledby="pitch-approval-required-title">
            <div className={styles.approvalIcon}>
              <Icon aria-hidden="true" icon={Lock} iconSize={IconSize.Custom} />
            </div>
            <div className={styles.approvalContent}>
              <h2 id="pitch-approval-required-title" className={styles.approvalTitle}>
                {approvalRequiredTitle}
              </h2>
              <p className={styles.approvalDescription}>{approvalRequiredDescription}</p>
              {canSubmitIdApplication && (
                <Link className={styles.approvalLink} to={`${xboxOnboardSiteLocationPrefix}/id`}>
                  {approvalRequiredLink}
                </Link>
              )}
            </div>
          </section>
        )}
      </PartnerPageLayout>
    </>
  );
};

export default PitchListPage;
